import React, { forwardRef, useEffect, useRef } from 'react';
import { rocketStart } from '../rocketStart';
import '../styles/rocket.css';

export const RocketAnimation = forwardRef<{ fly: () => void }>(
  (_props, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const rocketInstanceRef = useRef<ReturnType<typeof rocketStart> | null>(
      null,
    );
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rocketRef = useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => ({
      fly: () => rocketInstanceRef.current?.fly(),
    }));

    useEffect(() => {
      rocketInstanceRef.current = rocketStart({
        canvasElement: canvasRef.current,
        rocketElement: rocketRef.current,
        rocketWrapperElement: wrapperRef.current,
      });

      if (wrapperRef.current && rocketInstanceRef.current) {
        resizeObserverRef.current = new ResizeObserver(() => {
          rocketInstanceRef.current?.resize();
        });
        resizeObserverRef.current.observe(wrapperRef.current);
      }

      if (ref && typeof ref !== 'function') {
        ref.current = {
          fly: () => rocketInstanceRef.current?.fly(),
        };
      }

      return () => {
        if (rocketInstanceRef.current) {
          rocketInstanceRef.current.stop();
          rocketInstanceRef.current = null;
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
      };
    }, [ref]);

    return (
      <div
        className='relative flex-1 flex-col overflow-hidden'
        ref={wrapperRef}
      >
        <canvas id='canvas' ref={canvasRef} width='500' height='200' />
        <div id='rocket' ref={rocketRef}>
          <svg
            width='114px'
            height='197px'
            viewBox='663 230 114 197'
            role='graphics-symbol'
          >
            <g
              stroke='none'
              strokeWidth='1'
              fill='none'
              fillRule='evenodd'
              transform='translate(665.000000, 232.000000)'
            >
              <path
                d='M16.7962103,82.0294452 C16.7962103,82.0294452 11.9928769,107.148142 28.0619026,142.453075 C28.0619026,142.453075 13.4696974,145.591716 26.271441,187.186311 L21.0258513,187.095157 C21.0258513,187.095157 4.86374872,171.805955 0.864825641,141.346842 C0.864825641,141.346842 -4.41235385,105.354311 16.7962103,82.0294452'
                fill='#4b5971'
              />
              <path
                d='M16.7962103,82.0294452 C16.7962103,82.0294452 11.9928769,107.148142 28.0619026,142.453075 C28.0619026,142.453075 13.4696974,145.591716 26.271441,187.186311 L21.0258513,187.095157 C21.0258513,187.095157 4.86374872,171.805955 0.864825641,141.346842 C0.864825641,141.346842 -4.41235385,105.354311 16.7962103,82.0294452 L16.7962103,82.0294452 Z'
                stroke='#4b5971'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M15.7426359,84.3914726 C15.7426359,84.3914726 -3.30597949,104.506799 1.68802051,142.862032 C1.68802051,142.862032 5.4466359,170.878543 21.4671487,186.164368 L24.8653026,186.224012 C24.8653026,186.224012 -15.8865949,135.427312 15.7426359,84.3914726'
                fill='#FEFEFE'
              />
              <path
                d='M93.3160462,82.0294452 C93.3160462,82.0294452 98.1193795,107.148142 82.0503538,142.453075 C82.0503538,142.453075 96.642559,145.591716 83.8408154,187.186311 L89.0864051,187.095157 C89.0864051,187.095157 105.248508,171.805955 109.247431,141.346842 C109.247431,141.346842 114.52461,105.354311 93.3160462,82.0294452'
                fill='#4b5971'
              />
              <path
                d='M93.3160462,82.0294452 C93.3160462,82.0294452 98.1193795,107.148142 82.0503538,142.453075 C82.0503538,142.453075 96.642559,145.591716 83.8408154,187.186311 L89.0864051,187.095157 C89.0864051,187.095157 105.248508,171.805955 109.247431,141.346842 C109.247431,141.346842 114.52461,105.354311 93.3160462,82.0294452 L93.3160462,82.0294452 Z'
                stroke='#4b5971'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M94.3696205,84.3914726 C94.3696205,84.3914726 113.418236,104.506799 108.424236,142.862032 C108.424236,142.862032 104.665621,170.878543 88.6451077,186.164368 L85.2469538,186.224012 C85.2469538,186.224012 125.998851,135.427312 94.3696205,84.3914726'
                fill='#FEFEFE'
              />
              <path
                d='M56.0409385,0.28128484 C56.0409385,0.28128484 14.0643744,24.6639933 16.1086821,95.5619525 C16.1086821,95.5619525 16.1086821,129.322885 40.0554,159.707725 C40.0554,159.707725 54.6808872,156.126815 69.8422718,159.707725 C69.8422718,159.707725 92.0368872,139.826474 94.665041,91.0604948 C94.665041,91.0604948 98.3091436,33.2921624 56.0409385,0.28128484'
                fill='#FEFEFE'
              />
              <path
                d='M56.0409385,0.28128484 C56.0409385,0.28128484 14.0643744,24.6639933 16.1086821,95.5619525 C16.1086821,95.5619525 16.1086821,129.322885 40.0554,159.707725 C40.0554,159.707725 54.6808872,156.126815 69.8422718,159.707725 C69.8422718,159.707725 92.0368872,139.826474 94.665041,91.0604948 C94.665041,91.0604948 98.3091436,33.2921624 56.0409385,0.28128484 L56.0409385,0.28128484 Z'
                stroke='#4b5971'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M56.1172051,1.59419125 C56.1172051,1.59419125 78.1504872,16.411302 88.5982308,50.9228531 C88.5982308,50.9228531 98.3154615,79.244337 91.4525897,110.941914 C91.4525897,110.941914 87.8124359,140.412958 69.7064359,158.794098 C69.7064359,158.794098 59.5604872,155.807943 40.5214615,158.794098 C40.5214615,158.794098 32.3165897,149.392241 27.2058205,136.45055 C27.2058205,136.45055 36.2591026,151.455034 42.0992564,150.704978 C47.9399744,149.954923 60.5555641,148.45425 68.8202308,149.204305 C68.8202308,149.204305 91.5986923,138.857704 89.9926923,83.1831125 C89.9926923,83.1831125 90.431,41.9199376 56.1172051,1.59419125'
                fill='#DEEBF7'
              />
              <path
                d='M17.2857949,106.930384 C17.2857949,106.930384 53.1700513,91.6057338 93.0165641,106.930384'
                stroke='#4b5971'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M31.4382256,27.4755475 C31.4382256,27.4755475 51.6409949,16.5814571 79.3875077,27.4755475'
                stroke='#4b5971'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M33.9226462,150.704978 C33.9226462,150.704978 52.8499795,143.721529 76.6500308,150.704978'
                stroke='#4b5971'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M53.2851846,106.226075 C53.2851846,105.384302 53.9688769,104.702331 54.8127744,104.702331 C55.6561077,104.702331 56.3403641,105.384302 56.3403641,106.226075 C56.3403641,107.067847 55.6561077,107.749818 54.8127744,107.749818 C53.9688769,107.749818 53.2851846,107.067847 53.2851846,106.226075'
                fill='#DEEAF7'
              />
              <path
                d='M53.2851846,106.226075 C53.2851846,105.384302 53.9688769,104.702331 54.8127744,104.702331 C55.6561077,104.702331 56.3403641,105.384302 56.3403641,106.226075 C56.3403641,107.067847 55.6561077,107.749818 54.8127744,107.749818 C53.9688769,107.749818 53.2851846,107.067847 53.2851846,106.226075 L53.2851846,106.226075 Z'
                stroke='#4b5971'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M80.3916667,109.141669 C80.3916667,108.299896 81.075359,107.617925 81.9192564,107.617925 C82.7625897,107.617925 83.4468462,108.299896 83.4468462,109.141669 C83.4468462,109.983441 82.7625897,110.665412 81.9192564,110.665412 C81.075359,110.665412 80.3916667,109.983441 80.3916667,109.141669'
                fill='#DEEAF7'
              />
              <path
                d='M80.3916667,109.141669 C80.3916667,108.299896 81.075359,107.617925 81.9192564,107.617925 C82.7625897,107.617925 83.4468462,108.299896 83.4468462,109.141669 C83.4468462,109.983441 82.7625897,110.665412 81.9192564,110.665412 C81.075359,110.665412 80.3916667,109.983441 80.3916667,109.141669 L80.3916667,109.141669 Z'
                stroke='#4b5971'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M66.8524154,105.895161 C66.8524154,105.053389 67.5361077,104.371418 68.3800051,104.371418 C69.2233385,104.371418 69.9075949,105.053389 69.9075949,105.895161 C69.9075949,106.736934 69.2233385,107.418905 68.3800051,107.418905 C67.5361077,107.418905 66.8524154,106.736934 66.8524154,105.895161'
                fill='#DEEAF7'
              />
              <path
                d='M66.8524154,105.895161 C66.8524154,105.053389 67.5361077,104.371418 68.3800051,104.371418 C69.2233385,104.371418 69.9075949,105.053389 69.9075949,105.895161 C69.9075949,106.736934 69.2233385,107.418905 68.3800051,107.418905 C67.5361077,107.418905 66.8524154,106.736934 66.8524154,105.895161 L66.8524154,105.895161 Z'
                stroke='#4b5971'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M26.2347744,109.141669 C26.2347744,108.299896 26.9184667,107.617925 27.7623641,107.617925 C28.6056974,107.617925 29.2899538,108.299896 29.2899538,109.141669 C29.2899538,109.983441 28.6056974,110.665412 27.7623641,110.665412 C26.9184667,110.665412 26.2347744,109.983441 26.2347744,109.141669'
                fill='#DEEAF7'
              />
              <path
                d='M26.2347744,109.141669 C26.2347744,108.299896 26.9184667,107.617925 27.7623641,107.617925 C28.6056974,107.617925 29.2899538,108.299896 29.2899538,109.141669 C29.2899538,109.983441 28.6056974,110.665412 27.7623641,110.665412 C26.9184667,110.665412 26.2347744,109.983441 26.2347744,109.141669 L26.2347744,109.141669 Z'
                stroke='#4b5971'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M39.7739692,105.895161 C39.7739692,105.053389 40.4576615,104.371418 41.301559,104.371418 C42.1448923,104.371418 42.8291487,105.053389 42.8291487,105.895161 C42.8291487,106.736934 42.1448923,107.418905 41.301559,107.418905 C40.4576615,107.418905 39.7739692,106.736934 39.7739692,105.895161'
                fill='#DEEAF7'
              />
              <path
                d='M39.7739692,105.895161 C39.7739692,105.053389 40.4576615,104.371418 41.301559,104.371418 C42.1448923,104.371418 42.8291487,105.053389 42.8291487,105.895161 C42.8291487,106.736934 42.1448923,107.418905 41.301559,107.418905 C40.4576615,107.418905 39.7739692,106.736934 39.7739692,105.895161 L39.7739692,105.895161 Z'
                stroke='#4b5971'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M76.0885795,60.5465195 C76.0885795,72.2519977 66.5755538,81.7410706 54.8405282,81.7410706 C43.1060667,81.7410706 33.593041,72.2519977 33.593041,60.5465195 C33.593041,48.8410414 43.1060667,39.3519685 54.8405282,39.3519685 C66.5755538,39.3519685 76.0885795,48.8410414 76.0885795,60.5465195'
                fill='#FEFEFE'
              />
              <path
                d='M76.0885795,60.5465195 C76.0885795,72.2519977 66.5755538,81.7410706 54.8405282,81.7410706 C43.1060667,81.7410706 33.593041,72.2519977 33.593041,60.5465195 C33.593041,48.8410414 43.1060667,39.3519685 54.8405282,39.3519685 C66.5755538,39.3519685 76.0885795,48.8410414 76.0885795,60.5465195 L76.0885795,60.5465195 Z'
                stroke='#4b5971'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M69.7063231,60.5465195 C69.7063231,68.7357965 63.051041,75.374884 54.8405282,75.374884 C46.6305795,75.374884 39.9752974,68.7357965 39.9752974,60.5465195 C39.9752974,52.3572426 46.6305795,45.7181551 54.8405282,45.7181551 C63.051041,45.7181551 69.7063231,52.3572426 69.7063231,60.5465195'
                fill='#DEEBF7'
              />
              <path
                d='M69.7063231,60.5465195 C69.7063231,68.7357965 63.051041,75.374884 54.8405282,75.374884 C46.6305795,75.374884 39.9752974,68.7357965 39.9752974,60.5465195 C39.9752974,52.3572426 46.6305795,45.7181551 54.8405282,45.7181551 C63.051041,45.7181551 69.7063231,52.3572426 69.7063231,60.5465195 L69.7063231,60.5465195 Z'
                stroke='#4b5971'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M53.7083179,51.7667076 C53.7083179,53.3720399 52.4035487,54.6740866 50.7936,54.6740866 C49.1842154,54.6740866 47.8794462,53.3720399 47.8794462,51.7667076 C47.8794462,50.1613752 49.1842154,48.8593286 50.7936,48.8593286 C52.4035487,48.8593286 53.7083179,50.1613752 53.7083179,51.7667076'
                fill='#FEFEFE'
              />
              <path
                d='M55.7769385,73.6230854 C55.7769385,73.6230854 59.817041,73.2421496 63.2185795,70.4641875 C63.2185795,70.4641875 67.5452462,66.8793391 68.0439128,60.5463507'
                id='Stroke-55'
                stroke='#FEFEFE'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />

              <polygon
                fill='#4b5971'
                points='48.5653949 97.4377662 54.9583692 94.512944 61.3519077 97.4377662 57.6333436 192.930002 52.5823692 192.930002'
              />
              <polygon
                stroke='#4b5971'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
                points='48.5653949 97.4377662 54.9583692 94.512944 61.3519077 97.4377662 57.6333436 192.930002 52.5823692 192.930002'
              />
              <polygon
                fill='#FEFEFE'
                points='54.8687333 95.731995 49.7765795 97.9748464 53.3569385 192.108205 55.0526308 192.108205'
              />
              <text
                fill='#4b5971'
                fontFamily='Helvetica'
                fontSize='10'
                fontWeight='bold'
                id='svg_32'
                stroke='#4b5971'
                strokeWidth='0'
                textAnchor='middle'
                x='55.75'
                xmlSpace='preserve'
                y='92.5'
              >
                IP FABRIC
              </text>
            </g>
          </svg>
        </div>
      </div>
    );
  },
);
